import { bigcommerceObjectByUrl } from "app/gql/queries/bigcommerceObjectByUrl";
import { bigcommerceGetCategoryProductPage } from "app/gql/queries/bigcommerceGetCategoryProudctPage";
import { bigCommerceGQL } from "app/services/bigCommerceGqlService";
import {
	convertBigCommerceCategory,
	convertBigCommerceProduct,
	productMatchesVariantFilters,
} from "app/utils/bigcommerceApiUtil";
import { bigcommerceGetCategoryViaSearch } from "app/gql/queries/bigcommerceGetCategoryViaSearch";

/**
 * BC will not reutrn filtered products via Graphql if productAttributes has more then one item
 * We set one atribute as primary for BC Graphql and apply other filters localy
 * Pimary filter is one with erp. prefix, becasue it will return smaller sample size for local filtering
 */
const splitFilters = (
	filters: { attribute: string; values: string[] }[] = [],
) => {
	if (!filters.length) {
		return { primaryFilter: undefined, localFilters: [] };
	}

	const primary =
		filters.find((f) => f.attribute.startsWith("erp.")) ?? filters[0];

	const localFilters = filters.filter((f) => f !== primary);

	return {
		primaryFilter: [primary],
		localFilters,
	};
};

export const categoryApi = bigCommerceGQL.injectEndpoints({
	endpoints: (builder) => ({
		getCategory: builder.query({
			query: (path: string) => ({
				url: "",
				method: "POST",
				body: {
					...bigcommerceObjectByUrl(path),
				},
			}),
			transformResponse: (res: any) => {
				if (!res?.data) return undefined;
				return convertBigCommerceCategory(res.data.site.route.node);
			},
		}),
		getCategoryPage: builder.query({
			query: (args: { entityId: number; pageSize: number; cursor?: string }) => ({
				url: "",
				method: "POST",
				body: {
					...bigcommerceGetCategoryProductPage(args.entityId, args.pageSize, args.cursor),
				},
			}),
			transformResponse: (res: any) => {
				if (!res?.data) return { products: [], pageInfo: {} };

				const products = res.data.site.category.products.edges.map((edge: any) =>
					convertBigCommerceProduct(edge),
				);

				const pageInfo = { ...res.data.site.category.products.pageInfo };

				return { products, pageInfo };
			},
		}),
		getCategoryViaSearch: builder.query({
			query: (args: {
				entityId?: number;
				nextProductSetCursor?: string;
				filterAttributes?: { attribute: string; values: string[] }[];
				filterCategoryIds?: number[];
				filterPrice?: { minPrice?: number; maxPrice?: number };
				sort: string;
				searchTerm: string;
			}) => {
				const { primaryFilter } = splitFilters(args.filterAttributes);

				return {
					url: "",
					method: "POST",
					body: {
						...bigcommerceGetCategoryViaSearch({
							...(args.nextProductSetCursor && { nextProductSetCursor: args.nextProductSetCursor }),
							searchSubCategories: true,
							...(primaryFilter && { productAttributes: primaryFilter }),
							categoryEntityIds: args.entityId ? [args.entityId] : [...(args.filterCategoryIds || [])],
							...(args.filterPrice &&
								(args.filterPrice.minPrice || args.filterPrice.maxPrice) && {
								price: {
									...(args.filterPrice.minPrice && { minPrice: args.filterPrice.minPrice }),
									...(args.filterPrice.maxPrice && { maxPrice: args.filterPrice.maxPrice }),
								},
							}),
							searchTerm: args.searchTerm,
							sort: args.sort,
						}),
					},
				};
			},
			transformResponse: (res: any, _meta, args: any) => {
				if (!res?.data) return { products: [], pageInfo: {}, filters: [] };

				let products = res.data.site.search.searchProducts.products.edges.map((edge: any) =>
					convertBigCommerceProduct(edge),
				);

				const pageInfo = { ...res.data.site.search.searchProducts.products.pageInfo };

				if (args.filterAttributes?.length > 0) {
					products = products.filter((product: any) =>
						productMatchesVariantFilters(product, args.filterAttributes),
					);
				}

				const filters = res.data.site.search.searchProducts.filters.edges
					.map((filter: any) => {
						if (filter.node.__typename === "OtherSearchFilter") return null;

						const base = {
							label: filter.node.name,
							isCollapsedByDefault: filter.node.isCollapsedByDefault,
							__typename: filter.node.__typename,
						};

						if (filter.node.__typename === "CategorySearchFilter") {
							return {
								...base,
								values: filter.node.categories.edges.map((cat: any) => ({
									label: cat.node.name,
									value: cat.node.entityId,
									count: cat.node.productCount,
									isSelected: cat.node.isSelected,
									subCategories: cat.node.subCategories.edges.map((sub: any) => ({
										label: sub.node.name,
										value: sub.node.entityId,
										count: sub.node.productCount,
										isSelected: sub.node.isSelected,
									})),
								})),
							};
						}

						if (filter.node.__typename === "ProductAttributeSearchFilter") {
							return {
								...base,
								values: filter.node.attributes.edges.map((attr: any) => ({
									label: attr.node.value,
									value: attr.node.value,
									count: attr.node.productCount,
								})),
							};
						}

						if (filter.node.__typename === "PriceSearchFilter") {
							return {
								...base,
								...filter.node.selected,
							};
						}

						return null;
					})
					.filter(Boolean);

				return { products, pageInfo, filters };
			},
		}),
	}),
});

export const {
	useLazyGetCategoryQuery,
	useLazyGetCategoryPageQuery,
	useGetCategoryViaSearchQuery,
	useLazyGetCategoryViaSearchQuery,
} = categoryApi;
